<!--
  Multi-functional "image component" that either,

      1. displays the xml markup of an SVG image:

          <div
            ...
            v-html="showFull ? staticImage : image"
          />

  OR, shows a simple clickable panel that displays two button options:

      2. "Add Image" - let's a user pick a single image to include (see: <product-selector>)

      3. "Create Collage" - toggles open the "collage editor" for the user
                            (see: <imageWidgetOld> & ImageRowComponent.vue)

  ~~~~~

  Mockup: https://www.figma.com/file/XVpKitt8tcAkZlV6xc3PdJ/%E2%9C%85-Master?node-id=95%3A9545
          (Pages -> Sales Associate (Integrated) -> Edits -> Edits/Collage):

   Q. How does it save an image when you close the modal window?
   A.
      0. src/components/ImageBoxComponent.vue
      1. close()
      2. emitUpdated()
      3. contentUpdate(payload) in src/components/BlockComponent.vue

          - sets a 'content' prop, passed down to a dynamic
            <component :is="block.type"> (w/type 'image-widget')

          - it also enters a setter set(value) (in BlockComponent.vue which winds up
            mutating a whole bunch of other values

      4. finally, the image gets getting shown in the `v-html` part below,
         by embedding an SVG

-->
<template>
  <div>
    <div class="image-preview" :style="image ? 'background-color:transparent': 'background-color:white; border-radius: 5px;'">
      <div
        v-if="image"
        class="bg-white"
        v-html="showFull ? staticImage : image"
      />
      <div
        v-else
        class="bg-white"
      >
        <div
          v-if="isEmpty"
          class="d-flex justify-content-center align-items-center"
          style="height: 98px"
        >
          <base-link-with-icon
            size="r"
            icon="images-row"
            @click="showSelector = true"
          >
            Add Image
          </base-link-with-icon>
          <div class="vr" />
          <base-link-with-icon
            size="r"
            icon="collage"
            @click="showFull = true"
          >
            Create Collage
          </base-link-with-icon>
        </div>
      </div>
      <div
        v-if="!isEmpty"
        class="image-preview__icon"
        @click="showFull = true"
      >
        <font-awesome-icon icon="pen" />
      </div>
    </div>

    <!-- This modal appears when the user is adding one image and not opening the collage -->
    <product-selector
      :show="showSelector"
      location="add-image"
      disable-multi-select
      disable-multi-fetch-select
      title="Upload Image"
      uploader
      :disable-url-upload="true"
      :disable-catalog-upload="true"
      @cancel="showSelector = false"
      @complete="addSingleImage"
    />
    <transition name="fade">
      <div
        v-if="showFull"
        class="image-editor"
      >
        <!-- e.g. src/components/ImageRowComponent.vue -->
        <imageWidgetOld
          :aspect-ratio="canvasAspectRatio"
          v-bind="{ editSectionId: $attrs.editSectionId }"
          :content="content"
          :canvas-width="canvasWidth()"
          :dangerous-responsive-mode="dangerousResponsiveMode"
          @close="close"
          @contentUpdated="contentUpdated"
        />
      </div>
    </transition>
  </div>
</template>
<script>
import { fabric } from 'fabric';
import ProductSelector, { UPLOAD_MODE } from '@/components/ProductSelector';
import { getCollateralImageURL } from '@/_utilities/generalUseUtils';

import Whichbrowser from '@/components/mixins/whichbrowser';
import { sectionService, imageProcessingService } from '@/_services';

export default { // ImageWidget
  name: 'ImageWidget',
  components: {
    ProductSelector,
  },
  mixins: [
    Whichbrowser,
  ],
  props: {
    /*
      NOTE: per BlockComponent.vue:146 own computed prop 'content' get(),
            this winds up with an EditSection's 'canvas_content' when it sets...
              ...
              canvas: this.block.canvas_content
              ...
     */
    content: {
      type: Object,
      default: () => ({}),
    },
    dangerousResponsiveMode: {
      // used to easily toggle between the original/new way of sizing the collage editor!
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showFull: false, // toggles the collage editor on
      image: null,
      staticImage: null,
      canvas: null,
      png_render: null,
      copy: false,
      options: null,
      showSelector: false,
    };
  },
  computed: {
    isEmpty() {
      return this.content.svg_image === null
            && this.content.canvas === null
            && this.content.map === null;
    },
    canvasAspectRatio() {
      if (this.content.collage_width && this.content.collage_height) {
        return `${this.content.collage_width}:${this.content.collage_height}`;
      }
      return '5:4';
    },
  },
  watch: {
    content() {
      this.updateContent(this.content);
    },
    showFull() {
      this.staticImage = this.image;
      this.$store.commit('entries/updateDragglable', !this.showFull);

      // Quick solution to https://github.com/fabricjs/fabric.js/issues/7159
      if (this.showFull && this.isIPad) {
        window.savedLocation = document.documentElement.scrollTop;
        console.log('window.savedLocation', window.savedLocation);
        this.$nextTick().then(() => {
          window.scroll({
            top: 0,
            left: 0,
          });
        });
      } else {
        window.scroll({
          top: window.savedLocation,
          left: 0,
        });
      }
    },
  },
  mounted() {
    this.updateContent(this.content);
  },
  methods: {
    canvasWidth() {
      return this.content.collage_width || 800;
    },
    updateContent(content) {
      this.image = content.svg_image;
      this.canvas = content.canvas_content;
      this.map = content.map_content;
      this.options = content.options;
      this.png_render = content.png_render;
    },
    close() {
      this.showFull = false;
      this.emitUpdated();
    },
    contentUpdated(event) {
      this.image = event.image;
      this.canvas = event.canvas;
      this.map = event.map;
      this.options = event.options;
      this.png_render = event.png_render;
      if ('emitOptions' in event) {
        if ('force_save' in event.emitOptions && event.emitOptions.force_save) {
          console.log('contentUpdated()  ImageBoxComponent has "force_save", will emitUpdated()');
          this.emitUpdated();
        }
      }
    },
    emitUpdated() {
      this.$emit('contentUpdated', {
        image: this.image,
        canvas: this.canvas,
        map: this.map,
        options: this.options,
        png_render: this.png_render,
        copy: this.copy,
      });
    },
    addSingleImage({ event, mode }) {
      console.log('Uploading single image in mode ', mode, event);

      // Compute canvas dimensions
      const [w, h] = this.parseAspectRatio(this.canvasAspectRatio);
      const canvasW = this.canvasWidth();
      const canvasH = Math.floor((h * canvasW) / w);

      const img = event[0];

      const prepareImage = (fabricImage) => {
        // Contain image inside canvas (comparing image aspect ratio with the canvas's)
        const scale = fabricImage.width / fabricImage.height > w / h
          ? canvasW / fabricImage.width
          : canvasH / fabricImage.height;

        // Scale fabricImage
        fabricImage.set({
          scaleX: scale,
          scaleY: scale,
        });
        console.log('Creating canvas of size ', canvasW, canvasH);

        fabricImage.set({
          originX: 'center',
          originY: 'center',
          left: canvasW / 2,
          top: canvasH / 2,
        });

        // if (img?.site_url) fabricImage.set({ meta: { site_url: img.site_url } });
      };

      const createImageFromURL = (imageUrl) => {
        fabric.util.loadImage(imageUrl, (loadedImg) => {
          const image = new fabric.Image(loadedImg);
          prepareImage(image);

          // Append original selected object metadata
          image.meta = img;
          delete image.meta.image_url;

          // Draw image in fabric canvas
          const newCanvas = document.createElement('canvas');
          const mycanvas = new fabric.Canvas(newCanvas, { width: canvasW, height: canvasH });
          mycanvas.add(image);

          // Emit canvas changes to parent
          this.contentUpdated({
            image: mycanvas.toSVG({ width: canvasW || 800, height: canvasH || 650 }),
            canvas: mycanvas.toJSON(['meta']),
            map: this.htmlmap(mycanvas),
            options: this.options,
          });
          this.emitUpdated();
          console.log(`Added new ${image.width} x ${image.height} image`);
        }, { crossOrigin: 'anonymous' });
      };

      const createFromSharedCollage = () => {
        sectionService.fetchItemAsCollateral(img.pk).then((response) => {
          const canvasData = response.data;
          this.copy = true;

          canvasData.emitOptions = { force_save: true };
          this.updateContent(canvasData);
          this.emitUpdated();
        }).catch((error) => {
          console.error('Unexpected error: Unable to retrieve section', img.pk, 'as collateral');
        });
      };

      this.showSelector = false;

      if (mode === UPLOAD_MODE.ALBUM) {
        if (img.shared) {
          createFromSharedCollage();
          return;
        }
        createImageFromURL(getCollateralImageURL(img));
        return;
      }
      if ([UPLOAD_MODE.FETCH, UPLOAD_MODE.UPLOAD].includes(mode)) {
        imageProcessingService.uploadToS3(img.image_url).then((s3url) => {
          createImageFromURL(s3url);
        });
      }
    },
    htmlmap(canvas) {
      const map = {
        size: { width: canvas.width, height: canvas.height },
        elements: [],
      };
      const isNullish = (x) => x === null || x === undefined;
      canvas.getObjects().filter((targ) => targ.get('type') === 'image').forEach((targ) => {
        const rect = targ.getBoundingRect();
        if ([rect.top, rect.left, rect.width, rect.height].some(isNullish)) {
          // Skipping to address issue https://helloseer.atlassian.net/browse/SEER-107
          // not sure in what condition getBoundingRect will return a frame with null components
          // Skip for safety
          console.log('Skipping html frame because some bounding item is null');
          return;
        }
        if (targ.meta) {
          map.elements.push({ url: targ.meta.site_url, frame: rect });
        }
      });
      return map;
    },
  },
};
</script>
<style lang="scss">
  // @import "../styles/main.scss";

  .image-editor{
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    // background-color: darken( $blue-dark, 10% );
    // background-color: $bg-edits;
    // background: #f2f2f2 url('/img/grid.svg') repeat;
    z-index: $zindex-modal;
  }
  .image-preview{
    cursor: grab;
    position: relative;
    text-align: center;
    overflow: hidden;
    // width: 584px;
    display:flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .image-preview .card-body {
    padding: 0 !important;
  }

  .image-preview__icon{
    background-color: #f2f2f2;
    border-radius: 3px;
    position: absolute;
    top: 50%;
    left: 50%;
    padding: 20px 30px;
    font-size: 36px;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: all 0.2s;
  }
  .image-preview:hover .image-preview__icon{
    opacity: 1;
  }
</style>
<style scoped>
.vr {
  border-right: solid 1px #33333333;
  width: 1px;
  height: 25px;
  margin: 0 28px;
}
.icon {
  margin: 0 6px;
  transform: scale(1.1);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter,
.fade-leave-to

/* .fade-leave-active below version 2.1.8 */
 {
  opacity: 0;
}

</style>
